import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import { Close, Receipt } from "@mui/icons-material";

export default function DialogDetails({ onClose, idLoja, mes, ano }) {
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  const snackbar = useSnackbar();

  const handleSearch = async () => {
    setLoading(true);
    try {
      const mesNumero = String(mes.value).padStart(2, "0");
      const dataInicio = `${ano.value}-${mesNumero}-01`;
      const ultimoDia = new Date(ano.value, mes.value, 0).getDate();
      const dataFim = `${ano.value}-${mesNumero}-${ultimoDia}`;

      const response = await Api.get(
        "billing-charges-detail",
        "franquias",
        `?idPessoaLoja=${idLoja}&dataInicio=${dataInicio}&dataFim=${dataFim}`,
        true
      );
      setDetails(response.Result);
    } catch (error) {
      snackbar.showMessage(
        "Erro ao buscar os detalhes da cobrança: " + error.message,
        {
          severity: "error",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleSearch();
  }, [idLoja]);

  const processarDados = () => {
    console.log(details);
    if (!details?.Cobrancas || !Array.isArray(details.Cobrancas)) {
      return [];
    }

    const nfsMap = new Map();

    details.Cobrancas.forEach((cobranca) => {
      const tipoCobranca = cobranca.NomeTipoCobranca;

      cobranca.Itens?.forEach((item) => {
        const chave = `${item.Nf}-${item.Serie?.trim()}`;

        if (!nfsMap.has(chave)) {
          nfsMap.set(chave, {
            NF: item.Nf,
            Serie: item.Serie?.trim(),
            ValorBase: item.ValorBase,
            Royalties: 0,
            FundoPropaganda: 0,
            Tipo: item.TipoNf === "" ? "Venda" : item.TipoNf,
          });
        }

        const nf = nfsMap.get(chave);

        if (tipoCobranca === "Royalties") {
          nf.Royalties += item.ValorCalculado || 0;
        } else if (tipoCobranca === "Fundo de Propaganda") {
          nf.FundoPropaganda += item.ValorCalculado || 0;
        }
      });
    });

    return Array.from(nfsMap.values());
  };

  const calcularTotais = (dados) => {
    if (!dados || dados.length === 0) {
      return { totalValorBase: 0, totalRoyalties: 0, totalFundoPropaganda: 0 };
    }

    const totalValorBase = dados.reduce(
      (acc, row) =>
        acc +
        (row.Tipo.toLowerCase().includes("venda") ? row.ValorBase || 0 : 0),
      0
    );
    const totalDevolucao = dados.reduce(
      (acc, row) =>
        acc +
        (row.Tipo.toLowerCase().includes("devolução") ||
        row.Tipo.toLowerCase().includes("troca")
          ? row.ValorBase || 0
          : 0),
      0
    );
    const totalBase = totalValorBase - Math.abs(totalDevolucao);
    return { totalValorBase, totalDevolucao, totalBase };
  };

  const exportarCSV = () => {
    if (dadosProcessados.length === 0) {
      snackbar.showMessage("Não há dados para exportar", {
        severity: "warning",
      });
      return;
    }

    const formatCurrency = (value) => {
      return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value || 0);
    };

    let csvContent = "\uFEFF";
    csvContent += `Extrato da Cobrança de Serviços\n`;
    csvContent += `Loja;${details.NomeLoja}\n`;
    csvContent += `Período ${mes.label} / ${ano.label}`;

    // Cabeçalho da tabela
    csvContent += "NF;Série;Tipo;Valor base\n";

    // Dados das NFs
    dadosProcessados.forEach((row) => {
      csvContent += `${row.NF || "-"};${row.Serie || "-"};${
        row.Tipo || "-"
      };${formatCurrency(row.ValorBase)}\n`;
    });

    // Total Vendido
    csvContent += `;;;TOTAL VENDIDO;${formatCurrency(totalValorBase)}\n`;

    // Total Devolução
    csvContent += `;;;TOTAL DEVOLUÇÃO;${formatCurrency(totalDevolucao)}\n`;

    // Total Base
    csvContent += `;;;TOTAL BASE;${formatCurrency(totalBase)}\n`;

    // Cobranças individuais (Royalties, Fundo de Propaganda, etc)
    if (details.Cobrancas && Array.isArray(details.Cobrancas)) {
      details.Cobrancas.forEach((cobranca) => {
        csvContent += `;;;${cobranca.NomeTipoCobranca} / ${
          cobranca.PercentualRegra
        }%;${formatCurrency(cobranca.SubtotalCobranca)}\n`;
      });
    }

    // Total Cobrado
    csvContent += `;;;TOTAL COBRADO;${formatCurrency(totalCobrado)}\n`;

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    const fileName = `extrato_cobranca_${details.NomeLoja}_${mes.label}_${ano.label}.csv`;

    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    snackbar.showMessage("Arquivo CSV exportado com sucesso!", {
      severity: "success",
    });
  };

  const dadosProcessados = processarDados();
  const { totalValorBase, totalDevolucao, totalBase } =
    calcularTotais(dadosProcessados);
  const totalCobrado = details?.TotalGeral;

  return (
    <Dialog open onClose={onClose} maxWidth="lg">
      <DialogTitle>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3" color="primary">
            <Receipt style={{ marginRight: "12px" }} />
            Extrato da Cobrança de Serviços
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Typography variant="caption">Loja: {details.NomeLoja}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption">
              Período: {mes.label} / {ano.label}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ marginTop: "20px" }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>NF</TableCell>
                  <TableCell align="center">Série</TableCell>
                  <TableCell align="center">Tipo</TableCell>
                  <TableCell align="right">Valor base</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : dadosProcessados.length > 0 ? (
                  <>
                    {dadosProcessados.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.NF || "-"}</TableCell>
                        <TableCell align="center">{row.Serie || "-"}</TableCell>
                        <TableCell align="center">{row.Tipo || "-"}</TableCell>
                        <TableCell align="right">
                          R$ {MaskAndValid.maskPrice(row.ValorBase)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      style={{
                        backgroundColor: themeColorsDesingSystem.stone.light,
                      }}
                    >
                      <TableCell colSpan={3} align="right">
                        TOTAL VENDIDO
                      </TableCell>
                      <TableCell align="right">
                        {MaskAndValid.maskPrice(totalValorBase)}
                      </TableCell>
                    </TableRow>
                    <TableRow
                      style={{
                        backgroundColor: themeColorsDesingSystem.stone.light,
                      }}
                    >
                      <TableCell colSpan={3} align="right">
                        TOTAL DEVOLUÇÃO
                      </TableCell>
                      <TableCell align="right">
                        R$ {MaskAndValid.maskPrice(totalDevolucao)}
                      </TableCell>
                    </TableRow>
                    <TableRow
                      style={{
                        backgroundColor: themeColorsDesingSystem.stone.main,
                        fontWeight: "bold",
                      }}
                    >
                      <TableCell colSpan={3} align="right">
                        <strong>TOTAL BASE</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>R$ {MaskAndValid.maskPrice(totalBase)}</strong>
                      </TableCell>
                    </TableRow>
                    {details.Cobrancas.map((cobranca, index) => (
                      <TableRow key={index}>
                        <TableCell colSpan={3} align="right">
                          {cobranca.NomeTipoCobranca} /{" "}
                          {cobranca.PercentualRegra}%
                        </TableCell>
                        <TableCell align="right">
                          R$ {MaskAndValid.maskPrice(cobranca.SubtotalCobranca)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      style={{
                        backgroundColor:
                          themeColorsDesingSystem.secondary.active,
                        fontWeight: "bold",
                      }}
                    >
                      <TableCell colSpan={3} align="right">
                        TOTAL COBRADO
                      </TableCell>
                      <TableCell align="right">
                        R$ {MaskAndValid.maskPrice(totalCobrado)}
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            onClose();
          }}
        >
          Fechar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={exportarCSV}
          disabled={loading}
        >
          Exportar CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
}
